import React, { useMemo } from "react";
import ButtonLoading from "../../Button/ButtonLoading";
import { Box, Grid } from "@mui/material";
import { useCommunicationMedium } from "./CommunicationMediumProvider";
import SendForm from "./SendForm";
import ValidatorTextField from "../../Validator/ValidatorTextField";
import { formValue } from "../../../services/formServiceFunctions";
import { matchRegexp, requiredFieldDefault } from "../../../services/validationRules";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import MenuItem from "@mui/material/MenuItem";
import _ from "lodash";
import useForm from "../../../hooks/useForm";
import { getFaxNumber } from "./communicationHelperFunctions";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createResource, fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import { UseForm } from "../../../types/UseForm";
import { useDrawer } from "../../../provider/DrawerProvider";
import useDraftAutosave from "../../../hooks/useDraftAutosave";
import useEffectWithoutMount from "../../../hooks/useEffectWithoutMount";
import DraftAutosaveIndicator from "../../GlobalDrawer/DraftAutosaveIndicator";

export type FaxSendProps = {
  overwrittenHandleSave?: Function;
  faxDraft: {
    fax: string;
    faxAttachment: string;
  } | null;
};

export default function FaxSend({ faxDraft, overwrittenHandleSave }: FaxSendProps) {
  const { product, handleClose, assignedLawyerHasBea, recipientType } = useCommunicationMedium();
  const currentUser = useCurrentUser();
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const { drawerActionsNode, drawerHeaderNode } = useDrawer()

  let mediaObjectsFilter = {
    product: product.productClassName,
    productId: product.id,
  };
  const { data: mediaObjectsCollection } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilter),
    enabled: !!product,
  });

  const mediaObjects = _.get(mediaObjectsCollection, "hydra:member");

  const faxNumber = useMemo(() => {
    return getFaxNumber(product, recipientType);
  }, [product, recipientType]);

  const initialValues = {
    fax: faxDraft?.fax ?? "",
    faxAttachment: faxDraft?.faxAttachment ?? "",
  };

  const buildFaxDraftData = (currentValues: UseForm["values"]) => ({
    communicationMedium: "fax",
    recipientType: recipientType,
    fax: {
      fax: currentValues.fax,
      faxAttachment: currentValues.faxAttachment,
    },
  });

  const onSubmit = async ({ values }: { values: UseForm["values"] }) => {
    if (overwrittenHandleSave) {
      return;
    }
    try {
      await createMutation.mutateAsync({
        uri: "faxes",
        data: {
          mediaObject: values.faxAttachment,
          targetNumber: values.fax,
          backofficeCase: _.get(product, "backofficeCase.@id", "unknown"),
          createdBy: currentUser["@id"],
          forceSend: true,
        },
      });
    } catch (e) {
      alert(JSON.stringify(e));
    }
    handleClose();
  };

  const { values, handleChange, handleBlur, handleSubmit, isLoading, registerValidators, errors } = useForm({
    initialValues,
    onSubmit,
    identifier: "" + product.id,
    clearFormAfterSubmit: true,
    disableValidation: !!overwrittenHandleSave,
  });

  const { saveState, scheduleSave } = useDraftAutosave({
    enabled: !!overwrittenHandleSave,
    persist: async () => {
      await overwrittenHandleSave?.(buildFaxDraftData(values));
    },
  });

  useEffectWithoutMount(() => scheduleSave(), [values, recipientType]);

  // Must not run on mount: initial values may come from an AI-edited execution
  // step payload and would be clobbered (and autosaved) with the derived number.
  useEffectWithoutMount(() => {
    handleChange({
      target: {
        name: "fax",
        value: faxNumber,
      },
    });
  }, [faxNumber]);

  if (!drawerActionsNode) {
    return null;
  }

  return (
    <>
      <Grid container>
        {!assignedLawyerHasBea && (
          <Grid size={12}>
            <Box sx={{ color: "red", textAlign: "center" }}>
              Achtung: beA-Versand nicht möglich. Es sind keine beA-Daten für den zugeordneten Anwalt hinterlegt.
            </Box>
          </Grid>
        )}
        <Grid size={12}>
          <SendForm>
            <Grid size={12}>
              <ValidatorTextField
                onChange={handleChange}
                onBlur={handleBlur}
                name={"fax"}
                value={formValue(values, "fax")}
                label={"Faxnummer"}
                registerValidators={registerValidators}
                validators={[
                  ...requiredFieldDefault,
                  {
                    validator: matchRegexp,
                    params: [new RegExp(/^[1-9]\d+$/)],
                    message: "Bitte das Format beachten (Nur Ziffern ohne führende Null)",
                  },
                ]}
                error={!!_.get(errors, "fax")}
                helperText={_.get(errors, "fax")}
                disabled={recipientType !== "custom"}
                isHighlighted={false}
                initialDependentValidationFields={[]}
              />
            </Grid>
            <Grid size={12}>
              <ValidatorSelect
                label={"Dokument"}
                name={"faxAttachment"}
                value={formValue(values, "faxAttachment")}
                onChange={handleChange}
                onBlur={handleBlur}
                registerValidators={registerValidators}
                validators={requiredFieldDefault}
                error={!!_.get(errors, "faxAttachment")}
                helperText={_.get(errors, "faxAttachment")}
              >
                <MenuItem value={"none"} disabled>
                  {" "}
                </MenuItem>
                {_.map(mediaObjects, (mediaObject) => (
                  <MenuItem
                    key={mediaObject.id}
                    value={mediaObject["@id"]}
                    disabled={mediaObject.mimeType !== "application/pdf" || mediaObject.size > 2000000}
                  >
                    {mediaObject.description + " | " + dayjs(mediaObject.created).format("DD.MM.YYYY")}
                  </MenuItem>
                ))}
              </ValidatorSelect>
            </Grid>
          </SendForm>
        </Grid>
      </Grid>
      {overwrittenHandleSave
        ? drawerHeaderNode && createPortal(<DraftAutosaveIndicator saveState={saveState} />, drawerHeaderNode)
        : createPortal(
            <Grid container sx={{ width: "100%" }}>
              <Grid size={3} sx={{ display: { xs: "none", md: "block" } }} />
              <Grid size={{ xs: 12, md: 6 }} sx={{ marginTop: 6 }}>
                <ButtonLoading type={"submit"} variant={"contained"} onClick={handleSubmit} isLoading={isLoading}>
                  Nachricht Versenden
                </ButtonLoading>
              </Grid>
            </Grid>,
            drawerActionsNode,
          )}
    </>
  );
}
